import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2, X, Plus, Eye, EyeOff, CheckCircle, Upload } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const skillOptions = ['Commercial', 'Dialogue', 'Dance', 'Action', 'Voice Over', 'Modelling', 'OTT / Web', 'Print', 'Stunt', 'Child Work'];

const TalentRegistration = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [availableTags, setAvailableTags] = useState([]);
  
  const [accountData, setAccountData] = useState({ email: '', password: '', confirmPassword: '' });
  const [profileData, setProfileData] = useState({
    full_name: '', age: '', gender: '', location: '', height: '',
    languages: [], phone: '', look_tags: [], photos: [''],
    intro_video: '', showreel: '', budget_min: '', budget_max: '',
    experience_level: 'beginner', travel_willing: true,
    talent_type: '', years_experience: '', bio: '', availability: 'available',
    instagram: '', imdb_link: ''
  });
  const [newLanguage, setNewLanguage] = useState('');

  useEffect(() => { fetchLookTags(); }, []);

  const fetchLookTags = async () => {
    try {
      const response = await axios.get(`${API}/look-tags`);
      setAvailableTags(response.data.tags);
    } catch (error) { console.error('Error fetching tags:', error); }
  };

  const handleAccountChange = (field, value) => setAccountData(prev => ({ ...prev, [field]: value }));
  const handleProfileChange = (field, value) => setProfileData(prev => ({ ...prev, [field]: value }));

  const handleAddLanguage = () => {
    if (newLanguage.trim() && !profileData.languages.includes(newLanguage.trim())) {
      handleProfileChange('languages', [...profileData.languages, newLanguage.trim()]);
      setNewLanguage('');
    }
  };
  const handleRemoveLanguage = (lang) => handleProfileChange('languages', profileData.languages.filter(l => l !== lang));

  const handleTagToggle = (tag) => {
    const newTags = profileData.look_tags.includes(tag)
      ? profileData.look_tags.filter(t => t !== tag)
      : [...profileData.look_tags, tag];
    handleProfileChange('look_tags', newTags);
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;
    setUploading(true); setError('');
    try {
      const uploadedUrls = [];
      for (const file of files) {
        if (!file.type.startsWith('image/')) { setError('Please upload only image files'); continue; }
        const formData = new FormData();
        formData.append('file', file);
        formData.append('folder', 'talents');
        const response = await axios.post(`${API}/upload/image`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        if (response.data.url) uploadedUrls.push(response.data.url);
      }
      if (uploadedUrls.length > 0) {
        const currentPhotos = profileData.photos.filter(p => p.trim() !== '');
        handleProfileChange('photos', [...currentPhotos, ...uploadedUrls]);
      }
    } catch (err) { setError('Failed to upload images. Please try again.'); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const handleRemovePhoto = (index) => {
    const newPhotos = profileData.photos.filter((_, i) => i !== index);
    handleProfileChange('photos', newPhotos.length > 0 ? newPhotos : ['']);
  };

  const validateStep1 = () => {
    if (!accountData.email || !accountData.password) { setError('Email and password are required'); return false; }
    if (accountData.password.length < 6) { setError('Password must be at least 6 characters'); return false; }
    if (accountData.password !== accountData.confirmPassword) { setError('Passwords do not match'); return false; }
    if (!profileData.full_name || !profileData.age || !profileData.gender || !profileData.location) {
      setError('Please fill in all required fields'); return false;
    }
    return true;
  };

  const handleNextStep = () => { setError(''); if (validateStep1()) setStep(2); };

  const handleSubmit = async () => {
    setError(''); setSaving(true);
    try {
      const registerResponse = await axios.post(`${API}/auth/register`, {
        email: accountData.email, password: accountData.password,
        name: profileData.full_name, role: 'talent', phone: profileData.phone || null
      });
      const token = registerResponse.data.access_token;
      const cleanPhotos = profileData.photos.filter(p => p.trim() !== '');
      const talentPayload = {
        ...profileData, email: accountData.email, age: parseInt(profileData.age),
        photos: cleanPhotos,
        budget_min: profileData.budget_min ? parseInt(profileData.budget_min) : null,
        budget_max: profileData.budget_max ? parseInt(profileData.budget_max) : null
      };
      await axios.post(`${API}/talents`, talentPayload, { headers: { Authorization: `Bearer ${token}` } });
      setSuccess(true);
    } catch (err) {
      if (err.response?.data?.detail === 'Email already registered') setError('This email is already registered. Please login instead.');
      else setError(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally { setSaving(false); }
  };

  if (success) {
    return (
      <div className="flex items-center justify-center px-6" style={{ background: 'var(--ink)', minHeight: '100vh' }}>
        <div className="max-w-md w-full text-center">
          <div className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-8" style={{ background: 'rgba(46,204,138,.1)' }}>
            <CheckCircle className="w-12 h-12" style={{ color: 'var(--green)' }} />
          </div>
          <h1 className="font-display text-5xl mb-4" style={{ color: 'var(--cream)', letterSpacing: 3 }}>SUBMITTED!</h1>
          <p className="text-sm mb-8" style={{ color: 'var(--warm)', lineHeight: 1.7 }}>
            Thank you for registering with CastDeck. Your profile is pending approval. Our team will review your application and you'll be notified once approved.
          </p>
          <div className="space-y-3">
            <Link to="/login" className="btn-gold w-full py-3 block text-center text-xs" data-testid="login-after-submit">Login to Your Account</Link>
            <Link to="/" className="btn-ghost w-full py-3 block text-center text-xs" data-testid="home-after-submit">Back to Home</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: 'var(--ink)', minHeight: '100vh', position: 'relative', zIndex: 1 }}>
      {/* NAV */}
      <nav className="nav-glass fixed top-0 left-0 right-0 z-50">
        <div className="flex items-center justify-between px-6 h-14 max-w-[1400px] mx-auto">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-7 h-7 bg-[var(--gold)] flex items-center justify-center" style={{ clipPath: 'polygon(50% 0%,100% 25%,100% 75%,50% 100%,0% 75%,0% 25%)' }}>
              <span style={{ fontFamily: 'var(--display)', fontSize: 14, color: '#000', letterSpacing: 2 }}>C</span>
            </div>
            <span className="font-display text-xl" style={{ color: 'var(--cream)' }}>CASTDECK</span>
          </Link>
          <Link to="/login" className="text-xs" style={{ color: 'var(--warm)', fontFamily: 'var(--head)', fontWeight: 700 }}>
            Already registered? Login
          </Link>
        </div>
      </nav>

      {step === 1 ? (
        /* ═══ STEP 1: Account + Basic Info ═══ */
        <div className="max-w-3xl mx-auto px-6 pt-24 pb-12">
          <div className="mb-8">
            <div className="text-[10px] font-bold tracking-widest uppercase mb-3" style={{ color: 'var(--gold)', fontFamily: 'var(--head)' }}>Join CastDeck Free</div>
            <h1 className="font-display text-5xl mb-3" style={{ color: 'var(--cream)', letterSpacing: 3, lineHeight: 0.95 }}>
              GET<br/>CAST.<br/>NOT<br/><em className="font-serif">LOST.</em>
            </h1>
            <p className="text-sm max-w-md" style={{ color: 'var(--warm)', lineHeight: 1.7 }}>
              India's top production houses use CastDeck to find their next cast. Create your account to get started.
            </p>
          </div>

          {/* Progress */}
          <div className="flex items-center gap-2 mb-8">
            <div className="flex items-center gap-2 px-3 py-2" style={{ background: 'var(--gold-glow)', border: '1px solid var(--gold-line)' }}>
              <span className="text-xs font-bold" style={{ color: 'var(--gold)', fontFamily: 'var(--head)' }}>01</span>
              <span className="text-xs font-bold" style={{ color: 'var(--gold)', fontFamily: 'var(--head)' }}>Account</span>
            </div>
            <div className="h-px flex-1" style={{ background: 'rgba(255,255,255,.1)' }} />
            <div className="flex items-center gap-2 px-3 py-2" style={{ background: 'var(--faint)', border: '1px solid rgba(255,255,255,.06)' }}>
              <span className="text-xs font-bold" style={{ color: 'var(--muted-c)', fontFamily: 'var(--head)' }}>02</span>
              <span className="text-xs font-bold" style={{ color: 'var(--muted-c)', fontFamily: 'var(--head)' }}>Portfolio</span>
            </div>
            <div className="h-px flex-1" style={{ background: 'rgba(255,255,255,.06)' }} />
            <div className="flex items-center gap-2 px-3 py-2" style={{ background: 'var(--faint)', border: '1px solid rgba(255,255,255,.06)' }}>
              <span className="text-xs font-bold" style={{ color: 'var(--muted-c)', fontFamily: 'var(--head)' }}>03</span>
              <span className="text-xs font-bold" style={{ color: 'var(--muted-c)', fontFamily: 'var(--head)' }}>Go Live</span>
            </div>
          </div>

          {error && (
            <div className="mb-6 px-4 py-3 text-sm" style={{ background: 'rgba(224,86,86,.1)', border: '1px solid rgba(224,86,86,.2)', color: 'var(--red)' }} data-testid="error-message">
              {error}
            </div>
          )}

          {/* Account Section */}
          <div className="mb-6 p-6" style={{ background: 'var(--ink2)', border: '1px solid rgba(255,255,255,.06)' }}>
            <div className="text-[10px] font-bold tracking-widest uppercase mb-5" style={{ color: 'var(--gold)', fontFamily: 'var(--head)' }}>Create Account</div>
            <div className="form-row mb-3">
              <div className="col-span-2">
                <Label className="text-[10px] font-bold tracking-wider uppercase mb-2 block" style={{ color: 'var(--muted-c)', fontFamily: 'var(--head)' }}>Email *</Label>
                <Input value={accountData.email} onChange={(e) => handleAccountChange('email', e.target.value)} placeholder="your@email.com"
                  style={{ background: 'var(--surface)', borderColor: 'rgba(255,255,255,.08)', color: 'var(--cream)' }} data-testid="email-input" />
              </div>
            </div>
            <div className="form-row">
              <div>
                <Label className="text-[10px] font-bold tracking-wider uppercase mb-2 block" style={{ color: 'var(--muted-c)', fontFamily: 'var(--head)' }}>Password *</Label>
                <div className="relative">
                  <Input type={showPassword ? 'text' : 'password'} value={accountData.password} onChange={(e) => handleAccountChange('password', e.target.value)}
                    placeholder="Min 6 characters" style={{ background: 'var(--surface)', borderColor: 'rgba(255,255,255,.08)', color: 'var(--cream)', paddingRight: 40 }} data-testid="password-input" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-c)' }}>
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-[10px] font-bold tracking-wider uppercase mb-2 block" style={{ color: 'var(--muted-c)', fontFamily: 'var(--head)' }}>Confirm Password *</Label>
                <Input type={showPassword ? 'text' : 'password'} value={accountData.confirmPassword} onChange={(e) => handleAccountChange('confirmPassword', e.target.value)}
                  placeholder="Re-enter password" style={{ background: 'var(--surface)', borderColor: 'rgba(255,255,255,.08)', color: 'var(--cream)' }} data-testid="confirm-password-input" />
              </div>
            </div>
          </div>

          {/* Basic Info */}
          <div className="mb-6 p-6" style={{ background: 'var(--ink2)', border: '1px solid rgba(255,255,255,.06)' }}>
            <div className="text-[10px] font-bold tracking-widest uppercase mb-5" style={{ color: 'var(--gold)', fontFamily: 'var(--head)' }}>Basic Information</div>
            <div className="form-row mb-3">
              <div>
                <Label className="text-[10px] font-bold tracking-wider uppercase mb-2 block" style={{ color: 'var(--muted-c)', fontFamily: 'var(--head)' }}>Full Name *</Label>
                <Input value={profileData.full_name} onChange={(e) => handleProfileChange('full_name', e.target.value)} placeholder="Enter your full name"
                  style={{ background: 'var(--surface)', borderColor: 'rgba(255,255,255,.08)', color: 'var(--cream)' }} data-testid="name-input" />
              </div>
              <div>
                <Label className="text-[10px] font-bold tracking-wider uppercase mb-2 block" style={{ color: 'var(--muted-c)', fontFamily: 'var(--head)' }}>Age *</Label>
                <Input type="number" value={profileData.age} onChange={(e) => handleProfileChange('age', e.target.value)} placeholder="e.g., 25"
                  style={{ background: 'var(--surface)', borderColor: 'rgba(255,255,255,.08)', color: 'var(--cream)' }} data-testid="age-input" />
              </div>
            </div>
            <div className="form-row mb-3">
              <div>
                <Label className="text-[10px] font-bold tracking-wider uppercase mb-2 block" style={{ color: 'var(--muted-c)', fontFamily: 'var(--head)' }}>Gender *</Label>
                <Select value={profileData.gender} onValueChange={(v) => handleProfileChange('gender', v)}>
                  <SelectTrigger style={{ background: 'var(--surface)', borderColor: 'rgba(255,255,255,.08)' }} data-testid="gender-select"><SelectValue placeholder="Select gender" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="non-binary">Non-binary</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] font-bold tracking-wider uppercase mb-2 block" style={{ color: 'var(--muted-c)', fontFamily: 'var(--head)' }}>Location *</Label>
                <Input value={profileData.location} onChange={(e) => handleProfileChange('location', e.target.value)} placeholder="e.g., Mumbai, Delhi"
                  style={{ background: 'var(--surface)', borderColor: 'rgba(255,255,255,.08)', color: 'var(--cream)' }} data-testid="location-input" />
              </div>
            </div>
            <div className="form-row">
              <div>
                <Label className="text-[10px] font-bold tracking-wider uppercase mb-2 block" style={{ color: 'var(--muted-c)', fontFamily: 'var(--head)' }}>Phone</Label>
                <Input type="tel" value={profileData.phone} onChange={(e) => handleProfileChange('phone', e.target.value)} placeholder="+91 98765 43210"
                  style={{ background: 'var(--surface)', borderColor: 'rgba(255,255,255,.08)', color: 'var(--cream)' }} data-testid="phone-input" />
              </div>
              <div>
                <Label className="text-[10px] font-bold tracking-wider uppercase mb-2 block" style={{ color: 'var(--muted-c)', fontFamily: 'var(--head)' }}>Height</Label>
                <Input value={profileData.height} onChange={(e) => handleProfileChange('height', e.target.value)} placeholder="e.g., 5'8&quot;"
                  style={{ background: 'var(--surface)', borderColor: 'rgba(255,255,255,.08)', color: 'var(--cream)' }} data-testid="height-input" />
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button onClick={handleNextStep} className="btn-gold px-8 py-3 text-xs" data-testid="next-btn">Continue to Portfolio →</button>
          </div>
        </div>
      ) : (
        /* ═══ STEP 2: Portfolio & Details (Split Layout) ═══ */
        <div className="join-layout" style={{ marginTop: 56 }}>
          {/* Left Visual Panel */}
          <div className="join-left" style={{ background: 'var(--ink2)' }}>
            <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=800&q=40')", backgroundSize: 'cover', backgroundPosition: 'center' }} />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, var(--ink) 0%, transparent 30%), linear-gradient(to top, var(--ink) 0%, transparent 40%)' }} />
            <div className="relative z-10 p-10">
              <div className="text-[10px] font-bold tracking-widest uppercase mb-4" style={{ color: 'var(--gold)', fontFamily: 'var(--head)' }}>Join CastDeck Free</div>
              <h1 className="font-display text-6xl mb-4" style={{ color: 'var(--cream)', letterSpacing: 3, lineHeight: 0.88 }}>
                GET<br/>CAST.<br/>NOT<br/><em className="font-serif">LOST.</em>
              </h1>
              <p className="text-sm mb-8 max-w-xs" style={{ color: 'var(--warm)', lineHeight: 1.7 }}>
                India's top production houses use CastDeck to find their next cast. Your profile goes live within 24 hours.
              </p>
              <div className="flex flex-wrap gap-2 mb-8">
                {['Free forever', 'Live in 24hrs', 'NDA protected', 'No WhatsApp needed'].map((p, i) => (
                  <div key={i} className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold" 
                       style={{ background: 'var(--faint)', border: '1px solid rgba(255,255,255,.06)', color: 'var(--warm)', fontFamily: 'var(--head)' }}>
                    <span style={{ color: 'var(--gold)' }}>{i === 0 ? '✓' : i === 1 ? '⚡' : i === 2 ? '🔒' : '📱'}</span>
                    {p}
                  </div>
                ))}
              </div>
              {/* Steps */}
              <div className="grid grid-cols-3 gap-2">
                <div className="p-3 text-center" style={{ background: 'var(--gold-glow)', border: '1px solid var(--gold-line)' }}>
                  <div className="text-sm font-bold" style={{ color: 'var(--gold)', fontFamily: 'var(--head)' }}>✓</div>
                  <div className="text-[9px] font-bold uppercase tracking-wider mt-1" style={{ color: 'var(--gold)', fontFamily: 'var(--head)' }}>Account</div>
                </div>
                <div className="p-3 text-center" style={{ background: 'var(--gold-glow)', border: '1px solid var(--gold-line)' }}>
                  <div className="text-sm font-bold" style={{ color: 'var(--gold)', fontFamily: 'var(--head)' }}>02</div>
                  <div className="text-[9px] font-bold uppercase tracking-wider mt-1" style={{ color: 'var(--gold)', fontFamily: 'var(--head)' }}>Portfolio</div>
                </div>
                <div className="p-3 text-center" style={{ background: 'var(--faint)', border: '1px solid rgba(255,255,255,.06)' }}>
                  <div className="text-sm font-bold" style={{ color: 'var(--muted-c)', fontFamily: 'var(--head)' }}>03</div>
                  <div className="text-[9px] font-bold uppercase tracking-wider mt-1" style={{ color: 'var(--muted-c)', fontFamily: 'var(--head)' }}>Go Live</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Form Panel */}
          <div className="join-right">
            <div>
              <h2 className="text-2xl font-bold mb-1" style={{ fontFamily: 'var(--head)', color: 'var(--cream)' }}>Portfolio & Details</h2>
              <p className="text-xs mb-4" style={{ color: 'var(--muted-c)' }}>Step 2 of 2 — You're almost live.</p>
              <div className="prog-gold"><div className="prog-fill" style={{ width: '66%' }} /></div>
            </div>

            {error && (
              <div className="px-4 py-3 text-sm" style={{ background: 'rgba(224,86,86,.1)', border: '1px solid rgba(224,86,86,.2)', color: 'var(--red)' }} data-testid="error-message">
                {error}
              </div>
            )}

            {/* Photos */}
            <div>
              <div className="text-[10px] font-bold tracking-widest uppercase mb-3" style={{ color: 'var(--gold)', fontFamily: 'var(--head)' }}>Profile Photos</div>
              <div className="photo-drop" onClick={() => fileInputRef.current?.click()}>
                <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleFileUpload} className="hidden" data-testid="file-upload-input" />
                {uploading ? (
                  <div className="flex items-center gap-3"><Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--gold)' }} /><span className="text-sm" style={{ color: 'var(--warm)' }}>Uploading...</span></div>
                ) : (
                  <>
                    <span className="text-2xl">📸</span>
                    <div>
                      <div className="text-sm font-bold" style={{ color: 'var(--cream)', fontFamily: 'var(--head)' }}>Upload headshots & portfolio images</div>
                      <div className="text-[11px]" style={{ color: 'var(--muted-c)' }}>JPG or PNG · max 10MB · minimum 3 photos recommended</div>
                    </div>
                  </>
                )}
              </div>
              {profileData.photos.filter(p => p.trim() !== '').length > 0 && (
                <div className="grid grid-cols-4 gap-2 mt-3">
                  {profileData.photos.filter(p => p.trim() !== '').map((photo, i) => (
                    <div key={i} className="relative group aspect-square">
                      <img src={photo} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" style={{ border: '1px solid rgba(255,255,255,.06)' }} />
                      <button type="button" onClick={() => handleRemovePhoto(profileData.photos.indexOf(photo))}
                        className="absolute top-1 right-1 p-1 opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: 'var(--red)', color: '#fff' }}>
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Talent Details */}
            <div>
              <div className="text-[10px] font-bold tracking-widest uppercase mb-3" style={{ color: 'var(--gold)', fontFamily: 'var(--head)' }}>Talent Details</div>
              <div className="form-row mb-3">
                <div>
                  <Label className="text-[10px] font-bold tracking-wider uppercase mb-2 block" style={{ color: 'var(--muted-c)', fontFamily: 'var(--head)' }}>Talent Type</Label>
                  <Select value={profileData.talent_type || 'actor'} onValueChange={(v) => handleProfileChange('talent_type', v)}>
                    <SelectTrigger style={{ background: 'var(--surface)', borderColor: 'rgba(255,255,255,.08)' }}><SelectValue placeholder="Select type" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="actor">Actor</SelectItem>
                      <SelectItem value="model">Model</SelectItem>
                      <SelectItem value="performer">Performer</SelectItem>
                      <SelectItem value="singer">Singer</SelectItem>
                      <SelectItem value="dancer">Dancer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] font-bold tracking-wider uppercase mb-2 block" style={{ color: 'var(--muted-c)', fontFamily: 'var(--head)' }}>Years of Experience</Label>
                  <Input value={profileData.years_experience} onChange={(e) => handleProfileChange('years_experience', e.target.value)} placeholder="e.g., 4"
                    style={{ background: 'var(--surface)', borderColor: 'rgba(255,255,255,.08)', color: 'var(--cream)' }} />
                </div>
              </div>
              <div className="form-row mb-3">
                <div>
                  <Label className="text-[10px] font-bold tracking-wider uppercase mb-2 block" style={{ color: 'var(--muted-c)', fontFamily: 'var(--head)' }}>Languages</Label>
                  <Input value={newLanguage} onChange={(e) => setNewLanguage(e.target.value)} 
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddLanguage())}
                    placeholder="Hindi, English, Tamil…" style={{ background: 'var(--surface)', borderColor: 'rgba(255,255,255,.08)', color: 'var(--cream)' }} data-testid="language-input" />
                  {profileData.languages.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {profileData.languages.map((lang, i) => (
                        <span key={i} className="flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold" style={{ background: 'var(--gold-glow)', color: 'var(--gold)', border: '1px solid var(--gold-line)' }}>
                          {lang} <button type="button" onClick={() => handleRemoveLanguage(lang)}><X className="w-2.5 h-2.5" /></button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <Label className="text-[10px] font-bold tracking-wider uppercase mb-2 block" style={{ color: 'var(--muted-c)', fontFamily: 'var(--head)' }}>Availability</Label>
                  <Select value={profileData.availability} onValueChange={(v) => handleProfileChange('availability', v)}>
                    <SelectTrigger style={{ background: 'var(--surface)', borderColor: 'rgba(255,255,255,.08)' }}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="available">Available Now</SelectItem>
                      <SelectItem value="next_month">Next month</SelectItem>
                      <SelectItem value="busy">Not available</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="form-row full">
                <div>
                  <Label className="text-[10px] font-bold tracking-wider uppercase mb-2 block" style={{ color: 'var(--muted-c)', fontFamily: 'var(--head)' }}>Bio</Label>
                  <textarea value={profileData.bio} onChange={(e) => handleProfileChange('bio', e.target.value)}
                    placeholder="What makes you the right person for a project? Experience, range, unique skills…"
                    className="w-full h-24 p-3 text-sm resize-none"
                    style={{ background: 'var(--surface)', border: '1px solid rgba(255,255,255,.08)', color: 'var(--cream)', fontFamily: 'var(--body)', outline: 'none' }} />
                </div>
              </div>
            </div>

            {/* Skills */}
            <div>
              <div className="text-[10px] font-bold tracking-widest uppercase mb-3" style={{ color: 'var(--gold)', fontFamily: 'var(--head)' }}>Skills</div>
              <div className="flex flex-wrap gap-2">
                {(availableTags.length > 0 ? availableTags : skillOptions).map(tag => (
                  <button key={tag} type="button" onClick={() => handleTagToggle(tag)}
                    className={`skill-tag ${profileData.look_tags.includes(tag) ? 'active' : ''}`}
                    data-testid={`tag-${tag.toLowerCase().replace(/ /g, '-')}`}>
                    {tag}
                  </button>
                ))}
              </div>
            </div>

            {/* Links */}
            <div>
              <div className="text-[10px] font-bold tracking-widest uppercase mb-3" style={{ color: 'var(--gold)', fontFamily: 'var(--head)' }}>Links</div>
              <div className="form-row">
                <div>
                  <Label className="text-[10px] font-bold tracking-wider uppercase mb-2 block" style={{ color: 'var(--muted-c)', fontFamily: 'var(--head)' }}>Instagram</Label>
                  <Input value={profileData.instagram} onChange={(e) => handleProfileChange('instagram', e.target.value)} placeholder="@yourhandle"
                    style={{ background: 'var(--surface)', borderColor: 'rgba(255,255,255,.08)', color: 'var(--cream)' }} />
                </div>
                <div>
                  <Label className="text-[10px] font-bold tracking-wider uppercase mb-2 block" style={{ color: 'var(--muted-c)', fontFamily: 'var(--head)' }}>Showreel / IMDb</Label>
                  <Input value={profileData.showreel} onChange={(e) => handleProfileChange('showreel', e.target.value)} placeholder="https://"
                    style={{ background: 'var(--surface)', borderColor: 'rgba(255,255,255,.08)', color: 'var(--cream)' }} data-testid="showreel-input" />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between pt-2">
              <button onClick={() => setStep(1)} className="btn-ghost px-6 py-3 text-xs">← Back</button>
              <button onClick={handleSubmit} className="btn-gold px-8 py-3 text-xs flex items-center gap-2" disabled={saving} data-testid="submit-btn">
                {saving && <Loader2 className="w-4 h-4 spinner" />}
                SUBMIT PROFILE →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TalentRegistration;
